import {
  PlainDate,
  type PlainYearMonth,
  genPlainYearMonthList,
  getEndOfMonth,
} from "@sa-nu/shared-utils/date";

import { type MonthGrid, buildMonthGrids } from "./build-month-grids";

export type UnavailablePeriod = {
  /** 期間の初日 (含む) */
  startDate: PlainDate;
  /** 期間の最終日 (含む) */
  endDate: PlainDate;
  /** 注記の文言 */
  note: string;
};

/**
 * 予約できない期間による disabled 判定。
 * `checkInDate` は CO 選択中 (CI のみ選択済み) のときだけ渡す。それ以外は null。
 *
 * - 期間内の日は disabled
 * - CO 選択中は、CI より後ろにある期間の初日は CO として選べる (その前夜までは泊まれる) が、
 *   初日より後の日は期間をまたぐため CO にできない
 */
export function isDisabledByUnavailablePeriods(params: {
  date: PlainDate;
  unavailablePeriods: readonly UnavailablePeriod[];
  checkInDate: PlainDate | null;
}): boolean {
  const { date, unavailablePeriods, checkInDate } = params;
  return unavailablePeriods.some(({ startDate, endDate }) => {
    const isInUnavailablePeriod =
      PlainDate.compare(date, startDate) >= 0 &&
      PlainDate.compare(date, endDate) <= 0;
    if (
      checkInDate === null ||
      PlainDate.compare(checkInDate, startDate) >= 0
    ) {
      return isInUnavailablePeriod;
    }
    // CI が期間より前: 初日だけ CO 可、初日より後はすべて不可
    return PlainDate.compare(date, startDate) > 0;
  });
}

export type MonthItem =
  | {
      type: "month";
      /** 同じ年月が期間の前後に 2 回出ることがあるため、区間の開始日で一意にする */
      key: string;
      grid: MonthGrid;
    }
  | {
      type: "note";
      note: string;
      key: string;
      /** どの区間にも描画されない月 (初期スクロールの対象引き当て用) */
      omittedYearMonths: PlainYearMonth[];
    };

type RangeState = {
  /** 次の区間の開始日 */
  cursor: PlainDate;
  items: MonthItem[];
};

const noteKeyOf = ({ startDate }: UnavailablePeriod) =>
  `note-${startDate.toString()}`;

const maxDate = (a: PlainDate, b: PlainDate) =>
  PlainDate.compare(a, b) >= 0 ? a : b;

/**
 * 描画範囲 (minDate〜renderMaxDate) を予約できない期間で区切り、
 * 「月セクション or 注記」の並びを作る。
 *
 * - 区間ごとに buildMonthGrids で描画し、区間の端の月は週単位で切る
 * - 期間の手前の区間は期間の初日まで (初日は CO として選べるため)、
 *   後ろの区間は期間の最終日の翌日から
 * - 注記は期間ごとに 1 つ、区間と区間の間に置く
 * - 描画範囲と重ならない期間は出力しない
 */
export function buildMonthItems(params: {
  minDate: PlainDate;
  renderMaxDate: PlainDate;
  unavailablePeriods: readonly UnavailablePeriod[];
}): MonthItem[] {
  const { minDate, renderMaxDate, unavailablePeriods } = params;

  const visibleUnavailablePeriods = unavailablePeriods
    .filter(
      ({ startDate, endDate }) =>
        PlainDate.compare(endDate, minDate) >= 0 &&
        PlainDate.compare(startDate, renderMaxDate) <= 0,
    )
    .sort((a, b) => PlainDate.compare(a.startDate, b.startDate));

  // 期間が先頭に接する・重なる場合は手前の区間が空になる
  const monthItemsOfRange = (from: PlainDate, to: PlainDate): MonthItem[] =>
    PlainDate.compare(from, to) > 0
      ? []
      : buildMonthGrids(from, to).map((grid) => ({
          type: "month",
          key: `month-${from.toString()}-${grid.yearMonth.toString()}`,
          grid,
        }));

  const { cursor, items } = visibleUnavailablePeriods.reduce<RangeState>(
    (state, period) => ({
      cursor: maxDate(state.cursor, period.endDate.add({ days: 1 })),
      items: [
        ...state.items,
        ...monthItemsOfRange(state.cursor, period.startDate),
        {
          type: "note",
          note: period.note,
          key: noteKeyOf(period),
          omittedYearMonths: [],
        },
      ],
    }),
    { cursor: minDate, items: [] },
  );
  const allItems = [...items, ...monthItemsOfRange(cursor, renderMaxDate)];

  const renderedYearMonths = new Set(
    allItems.flatMap((item) =>
      item.type === "month" ? [item.grid.yearMonth.toString()] : [],
    ),
  );
  const omittedYearMonths = genPlainYearMonthList(
    minDate,
    renderMaxDate,
  ).filter((ym) => !renderedYearMonths.has(ym.toString()));

  // 省略された月は、その月と重なる最初の期間の注記に引き当てる
  const noteKeyOfOmittedMonth = (ym: PlainYearMonth) => {
    const first = ym.toPlainDate({ day: 1 });
    const last = getEndOfMonth(first);
    const period = visibleUnavailablePeriods.find(
      ({ startDate, endDate }) =>
        PlainDate.compare(startDate, last) <= 0 &&
        PlainDate.compare(endDate, first) >= 0,
    );
    return period === undefined ? undefined : noteKeyOf(period);
  };

  return allItems.map((item) =>
    item.type === "note"
      ? {
          ...item,
          omittedYearMonths: omittedYearMonths.filter(
            (ym) => noteKeyOfOmittedMonth(ym) === item.key,
          ),
        }
      : item,
  );
}
